/**
 * Base API utilities and common functionality
 */
import { getCsrfToken } from '@lib/utils';

import { ApiResponse, Problem, isProblem } from './types';

/**
 * Normalize all errors to Problem type
 */
export function toProblem(error: unknown): Problem {
  if (isProblem(error)) {
    return error;
  }

  if (error && typeof error === 'object' && 'response' in error) {
    const fetchError = error as { response?: Response; data?: unknown };
    const data = fetchError.data as Record<string, unknown> | undefined;

    if (data && typeof data === 'object' && 'type' in data && 'code' in data) {
      return data as unknown as Problem;
    }

    return {
      type: 'about:blank',
      title:
        (data && typeof data === 'object' && 'title' in data
          ? (data.title as string | undefined)
          : undefined) || 'Error',
      status: fetchError.response?.status ?? 0,
      code:
        (data && typeof data === 'object' && 'code' in data
          ? (data.code as string | undefined)
          : undefined) || 'UNKNOWN_ERROR',
      detail:
        (data && typeof data === 'object' && 'detail' in data
          ? (data.detail as string | undefined)
          : undefined) ||
        (data && typeof data === 'object' && 'message' in data
          ? (data.message as string | undefined)
          : undefined) ||
        (data && typeof data === 'object' && 'error' in data
          ? (data.error as string | undefined)
          : undefined) ||
        'An error occurred',
      request_id: fetchError.response?.headers?.get('x-request-id') ?? undefined,
      errors:
        data && typeof data === 'object' && 'errors' in data
          ? (data.errors as Record<string, string[]> | undefined)
          : undefined,
    };
  }

  if (error instanceof Error) {
    return {
      type: 'about:blank',
      title: 'Error',
      status: 0,
      code: 'UNKNOWN_ERROR',
      detail: error.message,
    };
  }

  return {
    type: 'about:blank',
    title: 'Error',
    status: 0,
    code: 'UNKNOWN_ERROR',
    detail: 'An unknown error occurred',
  };
}

/**
 * Unwrap success response data
 */
export function unwrapData<T>(response: ApiResponse<T>): T {
  return response.data;
}

const isFormDataBody = (body: unknown): body is FormData =>
  typeof FormData !== 'undefined' && body instanceof FormData;

const hasContentTypeHeader = (headers?: HeadersInit): boolean => {
  if (!headers) {
    return false;
  }

  if (headers instanceof Headers) {
    return headers.has('Content-Type');
  }

  if (Array.isArray(headers)) {
    return headers.some(([key]) => key.toLowerCase() === 'content-type');
  }

  return Object.keys(headers).some(key => key.toLowerCase() === 'content-type');
};

const headersToTuples = (headers?: HeadersInit): Array<[string, string | null | undefined]> => {
  if (!headers) {
    return [];
  }

  if (headers instanceof Headers) {
    return Array.from(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers;
  }

  return Object.entries(headers);
};

const mergeHeaders = (baseHeaders?: HeadersInit, overrideHeaders?: HeadersInit): HeadersInit => {
  const result = new Headers();

  headersToTuples(baseHeaders).forEach(([key, value]) => {
    if (value != null) {
      result.set(key, String(value));
    }
  });

  headersToTuples(overrideHeaders).forEach(([key, value]) => {
    if (value == null) {
      result.delete(key);
    } else {
      result.set(key, String(value));
    }
  });

  return Object.fromEntries(result.entries());
};

const defaultOptions = (options: RequestInit = {}): RequestInit => {
  const headers: Record<string, string> = {
    'X-CSRFToken': getCsrfToken(),
  };

  if (!isFormDataBody(options.body) && !hasContentTypeHeader(options.headers)) {
    headers['Content-Type'] = 'application/json';
  }

  return {
    headers,
    credentials: 'include',
  };
};

const normalizeApiPath = (url: string): string => {
  if (url.startsWith('/api/')) {
    return url.slice(4).startsWith('/') ? url.slice(4) : `/${url.slice(4)}`;
  }
  if (url === '/api') {
    return '/';
  }
  if (url.startsWith('/api')) {
    const sliced = url.slice(4);
    return sliced.startsWith('/') ? sliced : `/${sliced}`;
  }
  return url.startsWith('/') ? url : `/${url}`;
};

const mergeOptions = (options: RequestInit = {}): RequestInit => {
  const base = defaultOptions(options);
  return {
    ...base,
    ...options,
    headers: mergeHeaders(base.headers, options.headers),
  };
};

async function callApiRaw(method: string, url: string, init: RequestInit = {}): Promise<Response> {
  try {
    const response = await fetch(`/api${normalizeApiPath(url)}`, mergeOptions({ ...init, method }));

    if (!response.ok) {
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));
      throw toProblem({ response, data: errorData });
    }

    return response;
  } catch (error) {
    throw toProblem(error);
  }
}

async function callApi<T>(
  method: string,
  url: string,
  init: RequestInit = {}
): Promise<ApiResponse<T>> {
  const response = await callApiRaw(method, url, init);
  return handleEnvelopeResponse<T>(response);
}

/**
 * Type-safe GET request
 */
export async function get<T>(
  url: string,
  options?: { params?: Record<string, string> }
): Promise<T> {
  const queryString = options?.params ? `?${new URLSearchParams(options.params).toString()}` : '';
  const envelope = await callApi<T>('GET', `${url}${queryString}`);
  return unwrapData(envelope);
}

/**
 * Type-safe POST request
 */
export async function post<T>(url: string, data?: unknown): Promise<T> {
  const envelope = await callApi<T>('POST', url, {
    body: data ? JSON.stringify(data) : undefined,
  });
  return unwrapData(envelope);
}

/**
 * Type-safe PATCH request
 */
export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const envelope = await callApi<T>('PATCH', url, {
    body: data ? JSON.stringify(data) : undefined,
  });
  return unwrapData(envelope);
}

/**
 * Type-safe PUT request
 */
export async function put<T>(url: string, data?: unknown): Promise<T> {
  const envelope = await callApi<T>('PUT', url, {
    body: data ? JSON.stringify(data) : undefined,
  });
  return unwrapData(envelope);
}

/**
 * Type-safe DELETE request
 */
export async function del<T = void>(url: string): Promise<T> {
  const envelope = await callApi<T>('DELETE', url);
  return unwrapData(envelope);
}

async function handleEnvelopeResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (response.status === 204) {
    return { data: undefined as T } as ApiResponse<T>;
  }

  const json = (await response.json()) as ApiResponse<T>;
  return json;
}

/**
 * Request helper that returns the full success envelope (data + meta)
 */
export async function request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const { method, ...rest } = options;
  return callApi<T>(method ?? 'GET', url, rest);
}

/**
 * Request helper that returns the raw response. Useful for downloads where callers
 * need to read Blob or text bodies directly while retaining consistent error handling.
 */
export async function requestRaw(url: string, options: RequestInit = {}): Promise<Response> {
  const { method, ...rest } = options;
  return callApiRaw(method ?? 'GET', url, rest);
}
